/**
 * Proximity / Weather per-tab map layers (mockup §toggleAirportMap, §toggleAirspaceMap,
 * §toggleObstacleMap, §toggleWxSensorMap).
 *
 * Drone-scoped: each layer reads from the currently-selected drone's
 * proximity envelope. When no drone is selected the layers go empty.
 * Each layer is gated by a Zustand `ui.show*` toggle so the user can
 * flip an individual data layer on/off from inside the Proximity or
 * Weather tab.
 */
import mapboxgl from 'mapbox-gl';
import type { GeoJSONSource, Map as MapboxMap } from 'mapbox-gl';
import type { Feature, Polygon } from 'geojson';
import { circle as turfCircle } from '@turf/turf';
import { droneFilterKey, visibleFleet } from '@/lib/map/droneFilter';
import { useDronesStore } from '@/lib/store/drones';
import { useUiStore } from '@/lib/store/ui';
import { useWxStore } from '@/lib/store/wx';
import { degToCardinal } from '@/lib/format';

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

// Mockup `toggleAirportMap` popup body (L13094-L13099).
function buildAirportPopup(p: Record<string, unknown>): string {
  const icao = String(p.icao ?? '—');
  const name = String(p.name ?? '');
  const kind = String(p.kind ?? '');
  const typeLabel = kind === 'Heliport' ? 'Heliport' : kind === 'Certified' ? 'Certified Airport' : 'Registered';
  const distNm = p.distNm as number | null | undefined;
  return (
    `<div style="font-family:Inter,sans-serif;min-width:140px">` +
      `<div style="font-weight:700;font-size:0.846em">${escapeHtml(icao)}</div>` +
      `<div style="font-size:0.692em;color:#666;margin-bottom:3px">${escapeHtml(name)}</div>` +
      `<div style="font-size:0.769em"><b>Type:</b> ${escapeHtml(typeLabel)}</div>` +
      `<div style="font-size:0.769em"><b>Distance:</b> ${distNm != null ? `${distNm.toFixed(1)} nm` : '—'}</div>` +
    `</div>`
  );
}

// Mockup `toggleAirspaceMap` popup (L13157-L13164).
function buildAirspacePopup(p: Record<string, unknown>): string {
  const cls = String(p.class ?? '');
  const name = String(p.name ?? '');
  const floorFt = p.floorFt as number | undefined;
  const ceilFt = p.ceilFt as number | undefined;
  const distNm = p.distNm as number | null | undefined;
  const within = p.within === true;
  const col = String(p.color ?? '#6b7280');
  const altRow = (floorFt != null || ceilFt != null)
    ? `<div style="font-size:0.769em;color:#555;margin-bottom:3px">` +
      `${floorFt != null ? `${floorFt.toLocaleString()} ft` : '—'} – ` +
      `${ceilFt != null ? `${ceilFt.toLocaleString()} ft` : '—'}</div>`
    : '';
  return (
    `<div style="font-family:Inter,sans-serif;min-width:150px">` +
      `<div style="margin-bottom:4px">` +
        `<span style="display:inline-block;background:${col};color:#fff;border-radius:3px;` +
        `padding:1px 7px;font-size:0.692em;font-weight:700;margin-right:4px">Class ${escapeHtml(cls)}</span>` +
        `<b style="font-size:0.846em">${escapeHtml(name)}</b>` +
      `</div>` +
      altRow +
      (distNm != null
        ? `<div style="font-size:0.769em"><b>${distNm.toFixed(1)} nm</b> from aircraft</div>`
        : '') +
      (within
        ? `<div style="font-size:0.769em;color:${col};font-weight:700;margin-top:3px">` +
          `Aircraft is within this airspace</div>`
        : '') +
    `</div>`
  );
}

// Mockup `toggleObstacleMap` popup (L13044-L13051).
function buildObstaclePopup(p: Record<string, unknown>): string {
  const name = String(p.name ?? '—');
  const kind = String(p.kind ?? '');
  const aglFt = p.heightAglFt as number | null | undefined;
  const aglM = p.heightAglM as number | null | undefined;
  const aslFt = p.aslFt as number | null | undefined;
  const aslM = p.aslM as number | null | undefined;
  const distKm = p.distKm as number | null | undefined;
  const droneId = p.drone_id as string | undefined;
  const subtitle = droneId ? `${kind} · ${droneId}` : kind;
  return (
    `<div style="font-family:Inter,sans-serif;min-width:140px">` +
      `<div style="font-weight:700;font-size:0.846em;margin-bottom:2px">${escapeHtml(name)}</div>` +
      `<div style="font-size:0.692em;color:#666;margin-bottom:3px">${escapeHtml(subtitle)}</div>` +
      (aglFt != null
        ? `<div style="font-size:0.769em"><b>AGL:</b> ${aglFt} ft` +
          (aglM != null ? ` (${aglM} m)` : '') + `</div>`
        : '') +
      (aslFt != null
        ? `<div style="font-size:0.769em"><b>ASL:</b> ${aslFt} ft` +
          (aslM != null ? ` (${aslM} m)` : '') + `</div>`
        : '') +
      `<div style="font-size:0.769em"><b>Dist:</b> ${distKm != null ? `${distKm.toFixed(1)} km` : '—'}` +
      (droneId ? ` from ${escapeHtml(droneId)}` : '') + `</div>` +
    `</div>`
  );
}

// Mockup `toggleWxSensorMap` popup body (L12624-L12633): station name + bold
// header, "{location} · {source}" subtitle, Wind / Temp / Humidity / Pressure
// rows, optional Camera link. The mockup's `m/s` label is computed via
// km/h × 0.621371 (= mph) — that's a unit-label bug in the source. We
// faithfully reproduce the popup *shape* but show a corrected "X mph (Y m/s)"
// where the secondary value is real m/s (mph × 0.44704).
function buildWxPopup(p: Record<string, unknown>): string {
  const name = String(p.station_name ?? '—');
  const source = String(p.source ?? '');
  const location = (p.location as string | null | undefined) ?? '';
  const tempC = p.tempC as number | null | undefined;
  const windMph = p.windSpdMph as number | null | undefined;
  const windDir = p.windDirDeg as number | null | undefined;
  const humidity = p.humidityPct as number | null | undefined;
  const pressure = p.pressureHpa as number | null | undefined;
  const cameraUrl = (p.cameraUrl as string | null | undefined) ?? '';

  const subtitle = location ? `${escapeHtml(location)} · ${escapeHtml(source)}` : escapeHtml(source);

  const windRow = windMph != null
    ? (() => {
        const ms = windMph * 0.44704;
        const dir = windDir != null ? ` ${degToCardinal(windDir)}` : '';
        return `<div style="font-size:0.769em"><b>Wind:</b> ${Math.round(windMph)} mph (${ms.toFixed(1)} m/s)${dir}</div>`;
      })()
    : '';

  const tempRow = tempC != null
    ? `<div style="font-size:0.769em"><b>Temp:</b> ${tempC.toFixed(1)}°C</div>`
    : `<div style="font-size:0.769em"><b>Temp:</b> —</div>`;

  const humidityRow = humidity != null
    ? `<div style="font-size:0.769em"><b>Humidity:</b> ${Math.round(humidity)}%</div>`
    : '';

  const pressureRow = pressure != null
    ? `<div style="font-size:0.769em"><b>Pressure:</b> ${Math.round(pressure)} hPa</div>`
    : '';

  const cameraRow = cameraUrl
    ? `<div style="margin-top:4px"><a href="${escapeHtml(cameraUrl)}" target="_blank" rel="noopener noreferrer" style="font-size:0.692em;color:#2563eb">▶ Camera Feed</a></div>`
    : '';

  return (
    `<div style="font-family:Inter,sans-serif;min-width:160px">` +
      `<div style="font-weight:700;font-size:0.846em;margin-bottom:3px">${escapeHtml(name)}</div>` +
      `<div style="font-size:0.692em;color:#666;margin-bottom:4px">${subtitle}</div>` +
      windRow + tempRow + humidityRow + pressureRow + cameraRow +
    `</div>`
  );
}

const SRC_APT = 'src-prox-apt';
const SRC_APT_LINE = 'src-prox-apt-line';
const SRC_AS = 'src-prox-airspace';
const SRC_OBS = 'src-prox-obs';
const SRC_WX = 'src-wx-sensors';

const AIRSPACE_COLORS: Record<string, string> = {
  B: '#1d4ed8', C: '#c41a1a', D: '#7c3aed',
  E: '#059669', F: '#d97706', G: '#22a050', R: '#8b0000', A: '#7c3aed',
};

function selectedDrone() {
  const ds = useDronesStore.getState();
  const id = ds.selectedId;
  if (!id) return null;
  return ds.byId[id] ?? null;
}

export function ensureProximityLayers(map: MapboxMap) {
  // Airport point + line layers
  if (!map.getSource(SRC_APT)) {
    map.addSource(SRC_APT, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  }
  if (!map.getSource(SRC_APT_LINE)) {
    map.addSource(SRC_APT_LINE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  }
  if (!map.getLayer('lyr-prox-apt-line')) {
    map.addLayer({
      id: 'lyr-prox-apt-line',
      type: 'line',
      source: SRC_APT_LINE,
      paint: { 'line-color': '#1d4ed8', 'line-width': 1.5, 'line-dasharray': [3, 3], 'line-opacity': 0.7 },
    });
  }
  if (!map.getLayer('lyr-prox-apt-bg')) {
    map.addLayer({
      id: 'lyr-prox-apt-bg',
      type: 'circle',
      source: SRC_APT,
      paint: {
        'circle-radius': 11,
        'circle-color': ['match', ['get', 'kind'], 'Heliport', '#7c3aed', 'Certified', '#1d4ed8', /* Registered */ '#6b7280'],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    });
  }
  if (!map.getLayer('lyr-prox-apt')) {
    map.addLayer({
      id: 'lyr-prox-apt',
      type: 'symbol',
      source: SRC_APT,
      layout: {
        'text-field': ['get', 'icao'],
        'text-size': 9,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
      },
      paint: { 'text-color': '#ffffff' },
    });
  }

  // Airspace circles
  if (!map.getSource(SRC_AS)) {
    map.addSource(SRC_AS, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  }
  if (!map.getLayer('lyr-prox-as-fill')) {
    map.addLayer({
      id: 'lyr-prox-as-fill',
      type: 'fill',
      source: SRC_AS,
      paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.08 },
    });
  }
  if (!map.getLayer('lyr-prox-as-line')) {
    map.addLayer({
      id: 'lyr-prox-as-line',
      type: 'line',
      source: SRC_AS,
      paint: { 'line-color': ['get', 'color'], 'line-width': 2, 'line-dasharray': [4, 2] },
    });
  }

  // Obstacle triangles — symbol layer with a unicode glyph (▲) tinted amber
  if (!map.getSource(SRC_OBS)) {
    map.addSource(SRC_OBS, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  }
  if (!map.getLayer('lyr-prox-obs-bg')) {
    map.addLayer({
      id: 'lyr-prox-obs-bg',
      type: 'circle',
      source: SRC_OBS,
      paint: {
        'circle-radius': 11,
        'circle-color': '#d97706',
        'circle-stroke-width': 1.5,
        'circle-stroke-color': '#ffffff',
      },
    });
  }
  if (!map.getLayer('lyr-prox-obs')) {
    map.addLayer({
      id: 'lyr-prox-obs',
      type: 'symbol',
      source: SRC_OBS,
      layout: {
        'text-field': '!',
        'text-size': 12,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
      },
      paint: { 'text-color': '#ffffff' },
    });
  }

  // Wx sensors — mockup `toggleWxSensorMap` (L12619-L12622) uses a 26×26
  // diamond SVG colour-coded by source, white stroke, drop-shadow. We
  // pre-render one bitmap per source so the GL symbol layer can pick the
  // right diamond via an `icon-image` match expression — same selection
  // pattern the circle layer previously used for `circle-color`.
  ensureWxDiamondImages(map);
  if (!map.getSource(SRC_WX)) {
    map.addSource(SRC_WX, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  }
  if (!map.getLayer('lyr-wx-sensors')) {
    map.addLayer({
      id: 'lyr-wx-sensors',
      type: 'symbol',
      source: SRC_WX,
      layout: {
        'icon-image': ['match', ['get', 'source'],
          'Alberta511', 'wx-diamond-alberta511',
          'Netatmo', 'wx-diamond-netatmo',
          'MSC', 'wx-diamond-msc',
          /* default */ 'wx-diamond-default'],
        'icon-size': 1,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-anchor': 'center',
      },
    });
  }
}

/** Render a 26×26 diamond at 2× pixel ratio for retina sharpness. Matches
 *  mockup polygon `13,1 25,13 13,25 1,13` with `stroke-width=2` white. */
function makeDiamondImage(color: string): {
  width: number; height: number; data: Uint8Array;
} {
  const PR = 2;
  const size = 26 * PR;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return { width: size, height: size, data: new Uint8Array(size * size * 4) };
  }
  ctx.scale(PR, PR);
  ctx.shadowColor = 'rgba(0,0,0,0.4)';
  ctx.shadowBlur = 3;
  ctx.shadowOffsetY = 1;
  ctx.beginPath();
  ctx.moveTo(13, 1);
  ctx.lineTo(25, 13);
  ctx.lineTo(13, 25);
  ctx.lineTo(1, 13);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.lineWidth = 2;
  ctx.lineJoin = 'miter';
  ctx.strokeStyle = '#ffffff';
  ctx.stroke();
  const img = ctx.getImageData(0, 0, size, size);
  return { width: size, height: size, data: new Uint8Array(img.data.buffer.slice(0)) };
}

const WX_DIAMOND_COLORS: Record<string, string> = {
  alberta511: '#2563eb',
  netatmo: '#7c3aed',
  msc: '#0891b2',
  default: '#6b7280',
};

function ensureWxDiamondImages(map: MapboxMap) {
  for (const [key, color] of Object.entries(WX_DIAMOND_COLORS)) {
    const id = `wx-diamond-${key}`;
    if (map.hasImage(id)) continue;
    const img = makeDiamondImage(color);
    map.addImage(id, img, { pixelRatio: 2 });
  }
}

export function syncProximityLayers(map: MapboxMap) {
  // No isStyleLoaded gate — setData works on already-added GeoJSON
  // sources regardless of basemap tile-load state. Per-source getSource
  // checks below act as the readiness gate. See surveillance.ts for the
  // full rationale (slow tile fetches were stalling syncs for 30-60 s).
  const ui = useUiStore.getState();
  const d = selectedDrone();
  const p = d?.proximity ?? null;
  const droneLat = d?.telemetry.lat;
  const droneLng = d?.telemetry.lng;

  // Airports: points + dashed lines from drone
  const aptSrc = map.getSource(SRC_APT) as GeoJSONSource | undefined;
  const aptLineSrc = map.getSource(SRC_APT_LINE) as GeoJSONSource | undefined;
  if (aptSrc && aptLineSrc) {
    const aptFeatures: Feature[] = [];
    const lineFeatures: Feature[] = [];
    if (ui.showProxAirports && p && droneLat != null && droneLng != null) {
      for (const a of p.airports) {
        if (a.lat == null || a.lng == null) continue;
        aptFeatures.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [a.lng, a.lat] },
          properties: {
            icao: a.icao,
            name: a.name,
            kind: a.type,
            distNm: a.distNm,
            within: a.within,
          },
        });
        lineFeatures.push({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: [[droneLng, droneLat], [a.lng, a.lat]] },
          properties: {},
        });
      }
    }
    aptSrc.setData({ type: 'FeatureCollection', features: aptFeatures });
    aptLineSrc.setData({ type: 'FeatureCollection', features: lineFeatures });
  }

  // Airspaces: coloured circle polygons
  const asSrc = map.getSource(SRC_AS) as GeoJSONSource | undefined;
  if (asSrc) {
    const features: Feature<Polygon>[] = [];
    if (ui.showProxAirspaces && p) {
      for (const a of p.airspaces) {
        if (a.lat == null || a.lng == null || a.radiusM == null) continue;
        const c = turfCircle([a.lng, a.lat], a.radiusM, { units: 'meters', steps: 64 });
        c.properties = {
          class: a.class,
          color: AIRSPACE_COLORS[a.class] ?? '#6b7280',
          name: a.name,
          floorFt: a.floorFt,
          ceilFt: a.ceilFt,
          distNm: a.distNm,
          within: a.within,
        };
        features.push(c as Feature<Polygon>);
      }
    }
    asSrc.setData({ type: 'FeatureCollection', features });
  }

  // Obstacles. Two trigger paths (mockup parity):
  //   (a) Proximity tab "Show on Map" → only the selected drone's obstacles.
  //   (b) Drones-ctx topbar "Obstacles" pill → obstacles for ALL drones
  //       (or for the selected drone if one is selected, matching mockup
  //       `toggleObstacles` scope rule L4753).
  const obsSrc = map.getSource(SRC_OBS) as GeoJSONSource | undefined;
  if (obsSrc) {
    const features: Feature[] = [];
    if (ui.showProxObstacles && p) {
      for (const o of p.obstacles) {
        if (o.lat == null || o.lng == null) continue;
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [o.lng, o.lat] },
          properties: {
            name: o.name,
            kind: o.type,
            heightAglFt: o.heightAglFt,
            heightAglM: o.heightAglM,
            aslFt: o.aslFt,
            aslM: o.aslM,
            distKm: o.distKm,
            drone_id: d?.id ?? null,
          },
        });
      }
    } else if (ui.showObstacles && ui.activeContext === 'drones') {
      const sel = useDronesStore.getState().selectedId;
      const targets = sel
        ? [useDronesStore.getState().byId[sel]].filter(Boolean) as NonNullable<ReturnType<typeof selectedDrone>>[]
        : visibleFleet();
      for (const dd of targets) {
        for (const o of dd.proximity?.obstacles ?? []) {
          if (o.lat == null || o.lng == null) continue;
          features.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [o.lng, o.lat] },
            properties: {
              name: o.name,
              kind: o.type,
              heightAglFt: o.heightAglFt,
              heightAglM: o.heightAglM,
              aslFt: o.aslFt,
              aslM: o.aslM,
              distKm: o.distKm,
              drone_id: dd.id,
            },
          });
        }
      }
    }
    obsSrc.setData({ type: 'FeatureCollection', features });
  }

  // Wx sensors — global "Show on Map" enables every station, but
  // individual rows can be toggled via `useWxStore.perSensorOn` even when
  // global is off (mockup `toggleWxSensorMap` per-row behaviour).
  const wxSrc = map.getSource(SRC_WX) as GeoJSONSource | undefined;
  if (wxSrc) {
    const features: Feature[] = [];
    const wx = useWxStore.getState();
    for (const s of wx.sensors) {
      const perRow = wx.perSensorOn[s.station_name] ?? false;
      const shouldShow = ui.showWxSensors || perRow;
      if (!shouldShow) continue;
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [s.lng, s.lat] },
        properties: {
          source: s.source,
          station_name: s.station_name,
          tempC: s.tempC,
          windSpdMph: s.windSpdMph,
          windDirDeg: s.windDirDeg,
          // Optional enrichment surfaced by the mockup popup body. Null/
          // undefined values get filtered in the popup builder; we keep
          // them on the feature so future hover/tooltip work has them.
          location: s.location ?? null,
          humidityPct: s.humidityPct ?? null,
          pressureHpa: s.pressureHpa ?? null,
          cameraUrl: s.cameraUrl ?? null,
        },
      });
    }
    wxSrc.setData({ type: 'FeatureCollection', features });
  }
}

export function subscribeProximityLayers(map: MapboxMap): () => void {
  ensureProximityLayers(map);
  let rafId: number | null = null;
  // No `idle` retry. Each `setData` causes Mapbox to repaint, which
  // triggers another `idle` — wiring sync to idle would recurse at the
  // browser's frame rate. Source-readiness is handled by `attachCore`
  // running on `style.load`; every other retrigger comes from store
  // subscriptions below.
  syncProximityLayers(map);
  const schedule = () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => { rafId = null; syncProximityLayers(map); });
  };
  const unsubD = useDronesStore.subscribe((s) => `${s.selectedId}|${s.byId[s.selectedId ?? '']?.proximity?.received_at ?? ''}`, schedule);
  const unsubUi = useUiStore.subscribe(
    (s) => `${s.showProxAirports}-${s.showProxAirspaces}-${s.showProxObstacles}-${s.showWxSensors}-${s.showObstacles}-${droneFilterKey(s)}`,
    schedule,
  );
  const unsubWx = useWxStore.subscribe((s) => `${s.sensors.length}|${Object.keys(s.perSensorOn).length}|${Object.values(s.perSensorOn).filter(Boolean).length}`, schedule);

  // Mockup proximity popups (L4775 / L12635 / L13052 / L13093) — each Show-on-
  // Map toggle wires its own bindPopup. We share a single Popup instance and
  // wire one click handler per layer with the matching builder.
  const popup = new mapboxgl.Popup({
    closeButton: true,
    closeOnClick: true,
    maxWidth: '220px',
    offset: 12,
  });
  type Handler = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => void;
  const mkClick = (build: (p: Record<string, unknown>) => string): Handler => (e) => {
    const f = e.features?.[0];
    if (!f) return;
    popup.setLngLat(e.lngLat).setHTML(build((f.properties ?? {}) as Record<string, unknown>)).addTo(map);
  };
  const onAptClick = mkClick(buildAirportPopup);
  const onAsClick = mkClick(buildAirspacePopup);
  const onObsClick = mkClick(buildObstaclePopup);
  const onWxClick = mkClick(buildWxPopup);
  const onMouseEnter = () => { map.getCanvas().style.cursor = 'pointer'; };
  const onMouseLeave = () => { map.getCanvas().style.cursor = ''; };
  const clickLayers: Array<[string, Handler]> = [
    ['lyr-prox-apt-bg', onAptClick],
    ['lyr-prox-as-fill', onAsClick],
    ['lyr-prox-obs-bg', onObsClick],
    ['lyr-wx-sensors', onWxClick],
  ];
  for (const [id, h] of clickLayers) {
    map.on('click', id, h);
    map.on('mouseenter', id, onMouseEnter);
    map.on('mouseleave', id, onMouseLeave);
  }

  return () => {
    unsubD();
    unsubUi();
    unsubWx();
    for (const [id, h] of clickLayers) {
      map.off('click', id, h);
      map.off('mouseenter', id, onMouseEnter);
      map.off('mouseleave', id, onMouseLeave);
    }
    popup.remove();
  };
}
